import parse from 'html-react-parser';
import sanitizeHtml from 'sanitize-html';
import { getBaseUrl } from './functions';

function updateContentTag(text: string) {
	const baseUrl = getBaseUrl();

	return (
		text
			.replaceAll('&gt;', '>')
			.replaceAll('&lt;', '<')
			.replaceAll('&quot;', '"')
			.replaceAll('&#39;', '’')
			//.replaceAll('’', '’') // weird quote that generate a warning but still works (UI broken with other quote)
			.replaceAll('&amp;', '&')
			.replaceAll('&nbsp;', ' ')
			.replaceAll('<code></code>', ``)
			.replaceAll('<code><code class="inline-code">', `<code class="richtext rt-inlinecode">`)
			.replaceAll('<code class="inline-code"><code>', `<code class="richtext rt-inlinecode">`)
			.replaceAll('<code class="inline-code">', `<code class="richtext rt-inlinecode">`)
			.replaceAll('</code></code></code>', `</code>`)
			.replaceAll('</code></code>', `</code>`)
			.replaceAll(
				new RegExp(`<a class="richtext rt-link" target="_blank" rel="noopener noreferrer" href="${baseUrl}`, 'g'),
				`<a class="richtext rt-link" href="${baseUrl}`
			)
			.replaceAll('<a href', `<a class="richtext rt-link" target="_blank" rel="noopener noreferrer" href`)
	);
}

function removeContentTag(text: string) {
	return text
		.replaceAll('&nbsp;', ' ')
		.replaceAll('<code><code class="inline-code">', ``)
		.replaceAll('<code class="inline-code"><code>', ``)
		.replaceAll('<code class="inline-code">', ``)
		.replaceAll('<code>', ``)
		.replaceAll('</code>', ``)
		.replaceAll('<b>', ``)
		.replaceAll('</b>', ``);
}

const katexClasses = [
	'katex-version',
	'katex-mathml',
	'katex-html',
	'newline',
	'base',
	'strut',
	'textbf',
	'textit',
	'textrm',
	'textsf',
	'texttt',
	'mathnormal',
	'mathit',
	'mathrm',
	'mathbf',
	'boldsymbol',
	'amsrm',
	'mathbb',
	'textbb',
	'mathcal',
	'mathfrak',
	'textfrak',
	'mathtt',
	'mathscr',
	'textscr',
	'mathsf',
	'mathboldsf',
	'textboldsf',
	'mathitsf',
	'textitsf',
	'mainrm',
	'vlist-t',
	'vlist-r',
	'vlist',
	'pstrut',
	'vlist-t2',
	'vlist-s',
	'vbox',
	'hbox',
	'thinbox',
	'msupsub',
	'mfrac',
	'frac-line',
	'overline',
	'overline-line',
	'underline',
	'underline-line',
	'hline',
	'hdashline',
	'rule',
	'mspace',
	'llap',
	'rlap',
	'clap',
	'inner',
	'fix',
	'sqrt',
	'root',
	'sizing',
	'reset-size1',
	'size1',
	'fontsize-ensurer',
	'size2',
	'size3',
	'size4',
	'size5',
	'size6',
	'size7',
	'size8',
	'size9',
	'size10',
	'size11',
	'reset-size2',
	'reset-size3',
	'reset-size4',
	'reset-size5',
	'reset-size6',
	'reset-size7',
	'reset-size8',
	'reset-size9',
	'reset-size10',
	'reset-size11',
	'delimsizing',
	'mult',
	'delim-size1',
	'delim-size4',
	'nulldelimiter',
	'delimcenter',
	'op-symbol',
	'small-op',
	'large-op',
	'op-limits',
	'accent',
	'accent-body',
	'accent-full',
	'overlay',
	'mtable',
	'vertical-separator',
	'arraycolsep',
	'col-align-c',
	'col-align-l',
	'col-align-r',
	'svg-alignsvgsvgpathimg',
	'stretchy',
	'hide-tail',
	'halfarrow-left',
	'halfarrow-right',
	'brace-left',
	'brace-center',
	'brace-right',
	'x-arrow-pad',
	'cd-arrow-pad',
	'x-arrow',
	'mover',
	'munder',
	'boxpad',
	'fbox',
	'fcolorbox',
	'cancel-pad',
	'cancel-lap',
	'sout',
	'angl',
	'anglpad',
	'eqn-num',
	'mml-eqn-num',
	'mtr-glue',
	'cd-vert-arrow',
	'cd-label-left',
	'cd-label-right',
	'katex-display',
	'tag',
	'leqno',
	'fleqn',
	'katex',
	'mord',
	'mathnormal'
];

/* const getHtmlTags = (text: string | undefined, returnTag?: boolean) => {
	if (!text) {
		return undefined;
	}
	try {
		let sanitizeText = sanitizeHtml(text, {
			allowedClasses: {
				'*': ['inline-code']
			}
		});
		if (returnTag === false) {
			return removeContentTag(sanitizeText);
		}
		return parse(updateContentTag(sanitizeText));
	} catch (error) {
		console.error(error);
		console.log('text', text);
		return text;
	}
}; */

function sanitizeInlineCodeChevrons(text: string) {
	const codeTagPattern = /<code class="richtext rt-inlinecode">(.*?)<\/code>/g;

	const sanitizedText = text.replace(codeTagPattern, (_match, codeBlock: string) => {
		const sanitizedCodeBlock = codeBlock.replace(/[<>]/g, (char) => {
			if (char === '<') {
				return '<span class="richtext rt-inlinecode" style="background-color: unset; padding: 0;">&#x003C;</span>';
			} else if (char === '>') {
				return '<span class="richtext rt-inlinecode" style="background-color: unset; padding: 0;">&#x003E;</span>';
			}
			return char;
		});

		return `<code class="richtext rt-inlinecode">${sanitizedCodeBlock}</code>`;
	});

	return sanitizedText;
}

const getHtmlTags = (text: string | undefined, returnTag?: boolean, contentTagUpdated?: boolean) => {
	if (!text) {
		return undefined;
	}

	try {
		const textWithSanitizedInlineCodeChevrons = sanitizeInlineCodeChevrons(text);
		let sanitizeText = sanitizeHtml(textWithSanitizedInlineCodeChevrons, {
			// allowed all classed
			allowedClasses: {
				'*': ['inline-code', 'richtext', 'rt-inlinecode', 'quote', 'rt-link', ...katexClasses]
			},
			allowedTags: [
				'svg',
				'path',
				'semantics',
				'msup',
				'mn',
				'math',
				'mrow',
				'mi',
				'mo',
				'msub',
				'mfrac',
				'msqrt',
				'mroot',
				'mfenced',
				'menclose',
				'mstyle',
				'mtr',
				'mtext',
				'munderover',
				'munder',
				'mtable',
				'mtd',
				'msubsup',
				'maction',
				'merror',
				'mmultiscripts',
				'mover',
				'mpadded',
				'mphantom',
				'ms',
				'mspace',
				'span',
				'p',
				'br',
				'code',
				'pre',
				'img',
				'blockquote',
				'ul',
				'ol',
				'li',
				'h1',
				'h2',
				'h3',
				'h4',
				'h5',
				'h6',
				'hr',
				'a',
				'table',
				'thead',
				'tbody',
				'tr',
				'td',
				'th',
				'figure',
				'figcaption',
				'caption'
			],
			allowedAttributes: {
				math: ['xmlns'],
				encoding: ['application/x-tex'],
				svg: ['xmlns', 'preserveAspectRatio'],
				'*': ['class', 'href', 'src', 'alt', 'title', 'style', 'width', 'height', 'target', 'rel', 'id', 'data-mathml']
			}
		});
		if (returnTag === false) {
			return removeContentTag(sanitizeText);
		}

		if (contentTagUpdated) {
			return parse(sanitizeText);
		}

		return parse(updateContentTag(sanitizeText));
	} catch (error) {
		console.error(error);
		console.log('text', text);
		return text;
	}
};

export { getHtmlTags, removeContentTag, updateContentTag };
